import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Row, Col, Badge, CardBody } from 'reactstrap';

import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import Collapse from '@material-ui/core/Collapse';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

import './style.css';
import formatCurrency from 'utils/formatCurrency';
import { Card, IconButton, TextField, Tooltip } from '@material-ui/core';
import { FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';
import Subtitle from 'views/components/Subtitle';
import PrecificacaoModel from 'models/PrecificacaoModel';
import api from 'services/api';
import toast from 'react-hot-toast';
import { iPropostaDados } from '../..';

const SortableItem = SortableElement(
  ({
    registro,
    renderItem,
  }: {
    registro: PrecificacaoModel;
    renderItem: (item: PrecificacaoModel) => React.ReactElement;
  }) => {
    return renderItem(registro);
  },
);

const SortableList = SortableContainer(
  ({
    items,
    renderItem,
  }: {
    items: PrecificacaoModel[];
    renderItem: (item: PrecificacaoModel) => React.ReactElement;
  }) => {
    return (
      <tbody>
        {items.map((registro, index) => (
          <SortableItem
            key={registro.id}
            index={index}
            registro={registro}
            renderItem={renderItem}
            disabled={registro.tipo !== 'C'}
          />
        ))}
      </tbody>
    );
  },
);

export default function Resumo({
  informacaoProposta,
  isModoSimulacao,
  onChange,
  erroPosSalvar,
  ordemSalvaVersion,
}: {
  informacaoProposta: iPropostaDados;
  isModoSimulacao: boolean;
  onChange: (a: iPropostaDados) => void;
  erroPosSalvar: boolean;
  ordemSalvaVersion: number;
}): JSX.Element {
  const [itensAbertos, setItensAbertos] = useState<string[]>([]);
  const [modoOrdenacao, setModoOrdenacao] = useState(false);
  const [ordenacaoOriginal, setOrdenacaoOriginal] = useState<
    { id: string; ordem: number }[]
  >([]);

  const tabelaRef = useRef<HTMLTableElement>(null);

  const isMateriaisAvulsos = (item: PrecificacaoModel) =>
    item.descricaoResumo === 'Materiais Avulsos';

  const itens = [...informacaoProposta.precificacao];

  const materialAvulso = itens.find(item => isMateriaisAvulsos(item));

  const itensOrdenaveis = itens
    .filter(item => item !== materialAvulso)
    .sort((a, b) => a.ordem - b.ordem);

  if (materialAvulso) {
    itensOrdenaveis.push(materialAvulso);
  }

  const iniciarOrdenacao = () => {
    toast('Arraste os itens para alterar a ordem.', {
      id: 'toast-ordenacao',
      icon: '↕️',
      duration: 4000,
    });

    setOrdenacaoOriginal(
      informacaoProposta.precificacao.map(item => ({
        id: item.id,
        ordem: item.ordem,
      })),
    );

    setItensAbertos([]);

    setModoOrdenacao(true);
  };

  const cancelarOrdenacao = () => {
    toast.dismiss('toast-ordenacao');
    const precificacao = informacaoProposta.precificacao.map(item => {
      const original = ordenacaoOriginal.find(
        registro => registro.id === item.id,
      );

      return original
        ? {
            ...item,
            ordem: original.ordem,
          }
        : item;
    });

    onChange({
      ...informacaoProposta,
      precificacao,
    });

    setModoOrdenacao(false);
    setOrdenacaoOriginal([]);
  };

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) {
      return;
    }

    const quantidadeHabilitados = itensOrdenaveis.filter(
      item => item.tipo === 'C',
    ).length;

    if (newIndex >= quantidadeHabilitados) {
      return;
    }

    const novaOrdem = [...itensOrdenaveis];

    const [itemMovido] = novaOrdem.splice(oldIndex, 1);

    novaOrdem.splice(newIndex, 0, itemMovido);

    const ordemPorId = new Map(
      novaOrdem.map((item, index) => [item.id, index + 1]),
    );

    const precificacao = informacaoProposta.precificacao.map(item => {
      const novaOrdemItem = ordemPorId.get(item.id);

      if (novaOrdemItem === undefined) {
        return item;
      }

      return {
        ...item,
        ordem: novaOrdemItem,
      };
    });

    onChange({
      ...informacaoProposta,
      precificacao,
    });
  };

  const alternarCollapse = (id: string) => {
    setItensAbertos(prev =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id],
    );
  };

  const abrirTodos = () => {
    setItensAbertos(informacaoProposta.precificacao.map(item => item.id));
  };

  const fecharTodos = () => {
    setItensAbertos([]);
  };

  function handleChange(id: string, name: string, valor: number | string) {
    const registro = informacaoProposta.precificacao.find(
      item => item.id === id,
    );

    let novaLista = informacaoProposta.precificacao;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const registroValor = registro[name];

    const newRegistro = {
      ...registro,
      valorFinalComOutros: (registro?.valorFinalComOutros ?? 0) - registroValor,
    };

    if (registro) {
      novaLista = informacaoProposta.precificacao.map(item => {
        if (item.id === id) {
          const atualizacao = {
            ...item,
            [name]: valor,
          };

          // Quando a mudança for no motivo, não é necessário atualizar o valorFinalComOutros
          if (typeof registroValor === 'number') {
            atualizacao.valorFinalComOutros =
              Number(newRegistro.valorFinalComOutros) + Number(valor);
          }

          return atualizacao;
        }

        return item;
      });
    }

    onChange({
      ...informacaoProposta,
      precificacao: novaLista,
    });
  }

  const valorFinalOriginal =
    informacaoProposta.valores.valorFinalComOutros ?? 0;

  const valorFinalAtual = informacaoProposta.precificacao.reduce(
    (accumulator, currentValue) =>
      accumulator + (currentValue?.valorFinalComOutros ?? 0),
    0,
  );

  const valorFinalAtualSemArr = informacaoProposta.precificacao.reduce(
    (accumulator, currentValue) =>
      accumulator + (currentValue?.valorSemArr ?? 0),
    0,
  );

  // Definir a cor do texto "valor final" do equipamento: vermelho se mudou, padrão se não
  const corValorFinal =
    isModoSimulacao && valorFinalAtual !== valorFinalOriginal
      ? '#d32f2f' // vermelho
      : '#212529'; // cor padrão

  const allExpanded =
    informacaoProposta.precificacao.length > 0 &&
    itensAbertos.length === informacaoProposta.precificacao.length;

  const handleToggleAll = () => {
    if (allExpanded) {
      fecharTodos();
    } else {
      abrirTodos();
    }
  };

  async function salvarOrdenacao() {
    try {
      const registros = informacaoProposta.precificacao
        .filter(item => !isMateriaisAvulsos(item))
        .map(item => ({
          configuracaoId: item.configuracaoId,
          ordem: item.ordem,
        }));

      await api.put(`/proposta-precificacao/versao/salvar-ordem`, {
        dados: registros,
      });

      toast.success('Ordenação salva');
    } catch (error) {
      console.error(error);
    } finally {
      setModoOrdenacao(false);
      setOrdenacaoOriginal([]);
    }
  }

  useEffect(() => {
    setModoOrdenacao(false);
    setOrdenacaoOriginal([]);
  }, [ordemSalvaVersion]);

  const renderLinhaItem = (item: PrecificacaoModel): React.ReactElement => {
    const podeMover = item.tipo === 'C';

    return (
      <tr
        className="linha-item"
        onClick={() => {
          if (!modoOrdenacao) {
            alternarCollapse(item.id);
          }
        }}
        style={{
          cursor: modoOrdenacao ? 'grab' : 'default',
          userSelect: modoOrdenacao ? 'none' : undefined,
          opacity: modoOrdenacao && !podeMover ? 0.5 : 1,
        }}
      >
        <td className="td-tag">
          <div className="tag-container">
            {!modoOrdenacao ? (
              <Button
                type="button"
                color="link"
                className="btn-icon"
                style={{
                  padding: 0,
                  margin: 0,
                  lineHeight: 1,
                  minHeight: 0,
                  height: 'auto',
                }}
                onClick={event => {
                  event.stopPropagation();
                  alternarCollapse(item.id);
                }}
              >
                {itensAbertos.includes(item.id) ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </Button>
            ) : (
              podeMover && (
                <Button
                  type="button"
                  color="link"
                  className="btn-icon"
                  style={{
                    padding: 0,
                    margin: 0,
                    lineHeight: 1,
                    minHeight: 0,
                    height: 'auto',
                  }}
                  onClick={event => {
                    event.stopPropagation();
                  }}
                >
                  ↕️
                </Button>
              )
            )}

            <div className="tag-info">
              <strong>{item?.tag ?? ''}</strong>

              {item.subTag && (
                <strong className="sub-tag">({item?.subTag ?? ''})</strong>
              )}
            </div>
          </div>
        </td>

        <td
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {item.descricaoResumo}
          {item.configuracaoId ? ` (#${item.configuracaoId})` : ''}
        </td>

        <td className="text-center retirar-espaco">
          {item.percentualIPI?.toString().replace('.', ',')}%
        </td>

        <td className="text-center retirar-espaco">
          {item.percentualICMS?.toString().replace('.', ',')}%
        </td>

        <td className="text-center retirar-espaco">
          {item.percentualPIS?.toString().replace('.', ',')}%
        </td>

        <td className="text-center retirar-espaco">
          {item.percentualST?.toString().replace('.', ',')}%
        </td>

        <td className="text-center retirar-espaco">
          {item.percentualISS?.toString().replace('.', ',')}%
        </td>

        <td className="text-center retirar-espaco">
          {formatCurrency(item.valorFinalComOutros, 2)}
        </td>

        <td className="text-center retirar-espaco">
          {!isModoSimulacao || !item.configuracaoId ? (
            item.faturamento ?? ''
          ) : (
            <TextField
              fullWidth
              type="text"
              style={{
                fontSize: '0.8rem',
              }}
              value={item.faturamento ?? null}
              onClick={event => event.stopPropagation()}
              onChange={e =>
                handleChange(item.id, 'faturamento', e.target.value)
              }
              disabled={!isModoSimulacao}
              InputProps={{
                style: {
                  padding: 10,
                  fontSize: '0.9rem',
                },
              }}
            />
          )}
        </td>

        <td className="text-center retirar-espaco">{item.finame}</td>

        <td className="text-center retirar-espaco">{item.ncm}</td>
      </tr>
    );
  };

  const renderCollapse = (item: PrecificacaoModel) => {
    if (!itensAbertos.includes(item.id)) {
      return null;
    }

    return (
      <tr className="linha-collapse">
        <td colSpan={11}>
          <Collapse in={itensAbertos.includes(item.id)}>
            <div className="conteudo-collapse">
              {/* Primeira linha */}
              <Row
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr 1fr',
                  gap: '16px',
                  marginLeft: '4px',
                  marginRight: '4px',
                }}
              >
                <Col
                  style={{
                    padding: 0,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    fullWidth
                    label={
                      <span
                        style={{
                          color: '#374151',
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: 1.5,
                        }}
                      >
                        Valor Outros (R$)
                      </span>
                    }
                    type="number"
                    value={item.valorOutros ?? 0}
                    onChange={e =>
                      handleChange(
                        item.id,
                        'valorOutros',
                        Number(e.target.value),
                      )
                    }
                    disabled={!isModoSimulacao}
                    InputProps={{
                      style: { padding: 10 },
                    }}
                  />
                </Col>

                <Col
                  style={{
                    padding: 0,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    fullWidth
                    label={
                      <span
                        className={`collapse-label ${
                          item.valorOutros !== 0 &&
                          item.motivoOutros === '' &&
                          erroPosSalvar
                            ? 'collapse-label-error'
                            : ''
                        }`}
                        style={{
                          color: '#374151',
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: 1.5,
                        }}
                      >
                        Motivo Outros
                      </span>
                    }
                    type="text"
                    value={item.motivoOutros ?? ''}
                    onChange={e =>
                      handleChange(item.id, 'motivoOutros', e.target.value)
                    }
                    disabled={!isModoSimulacao}
                    InputProps={{
                      style: { padding: 10 },
                    }}
                    error={
                      item.valorOutros !== 0 &&
                      item.motivoOutros === '' &&
                      erroPosSalvar
                    }
                    helperText={
                      item.valorOutros !== 0 &&
                      item.motivoOutros === '' &&
                      erroPosSalvar
                        ? 'Obrigatório.'
                        : ''
                    }
                  />
                </Col>

                <Col
                  style={{
                    padding: 0,
                    minWidth: 0,
                  }}
                  hidden={!informacaoProposta.outros?.isRegramentoGeral}
                >
                  <TextField
                    fullWidth
                    label={
                      <span
                        className="collapse-label"
                        style={{
                          color: '#374151',
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: 1.5,
                        }}
                      >
                        Valor Faturamento Direto (R$)
                      </span>
                    }
                    type="number"
                    value={item.valorFaturamentoDireto ?? 0}
                    onChange={e =>
                      handleChange(
                        item.id,
                        'valorFaturamentoDireto',
                        Number(e.target.value),
                      )
                    }
                    disabled={!isModoSimulacao}
                    InputProps={{
                      style: { padding: 10 },
                    }}
                  />
                </Col>
              </Row>

              {informacaoProposta.isExterior && (
                <Row
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '16px',
                    marginTop: '16px',
                    marginLeft: '4px',
                    marginRight: '4px',
                  }}
                >
                  <Col
                    style={{
                      padding: 0,
                      minWidth: 0,
                    }}
                  >
                    <TextField
                      fullWidth
                      label={
                        <span
                          className="collapse-label"
                          style={{
                            color: '#374151',
                            fontWeight: 700,
                            fontSize: '1rem',
                            lineHeight: 1.5,
                          }}
                        >
                          Valor Seguro (R$)
                        </span>
                      }
                      type="number"
                      value={item.valorSeguro ?? 0}
                      onChange={e =>
                        handleChange(
                          item.id,
                          'valorSeguro',
                          Number(e.target.value),
                        )
                      }
                      disabled={!isModoSimulacao}
                      InputProps={{
                        style: { padding: 10 },
                      }}
                    />
                  </Col>

                  <Col
                    style={{
                      padding: 0,
                      minWidth: 0,
                    }}
                  >
                    <TextField
                      fullWidth
                      label={
                        <span
                          className="collapse-label"
                          style={{
                            color: '#374151',
                            fontWeight: 700,
                            fontSize: '1rem',
                            lineHeight: 1.5,
                          }}
                        >
                          Valor Despachante (R$)
                        </span>
                      }
                      type="number"
                      value={item.valorDespachante ?? 0}
                      onChange={e =>
                        handleChange(
                          item.id,
                          'valorDespachante',
                          Number(e.target.value),
                        )
                      }
                      disabled={!isModoSimulacao}
                      InputProps={{
                        style: { padding: 10 },
                      }}
                    />
                  </Col>
                </Row>
              )}
            </div>
          </Collapse>
        </td>
      </tr>
    );
  };

  const handleCopyBtn = async () => {
    if (!tabelaRef.current) return;

    const tabelaOriginal = tabelaRef.current.querySelector('table');

    if (!tabelaOriginal) return;

    const tabela = tabelaOriginal.cloneNode(true) as HTMLTableElement;

    tabela.querySelector('tfoot')?.remove();

    tabela.style.margin = '0';
    tabela.style.padding = '0';
    tabela.style.borderCollapse = 'collapse';
    tabela.style.tableLayout = 'fixed';
    tabela.style.border = '1px solid #444';

    tabela.style.margin = '0';
    tabela.style.marginLeft = '0';
    tabela.style.marginRight = '0';
    tabela.style.padding = '0';
    tabela.style.textAlign = 'left';

    tabela.style.width = '710px';
    tabela.style.maxWidth = '710px';
    tabela.style.minWidth = '710px';
    tabela.setAttribute('width', '710');

    const larguras = [
      64, // TAG
      165, // DESCRIÇÃO

      42, // IPI
      42, // ICMS
      42, // PIS COFINS
      42, // ST
      42, // ISS

      78, // VALOR TOTAL
      70, // FATURAMENTO
      57, // FINAME
      68, // NCM
    ];

    const colgroup = document.createElement('colgroup');

    larguras.forEach(largura => {
      const col = document.createElement('col');

      col.style.width = `${largura}px`;
      col.setAttribute('width', String(largura));

      colgroup.appendChild(col);
    });

    tabela.insertBefore(colgroup, tabela.firstChild);

    // =========================
    // CÉLULAS
    // =========================

    tabela.querySelectorAll('th, td').forEach(cell => {
      const elemento = cell as HTMLElement;

      elemento.style.padding = '2px 3px';
      elemento.style.margin = '0';
      elemento.style.fontSize = '10pt';
      elemento.style.lineHeight = '1';
      elemento.style.border = '1px solid #444';
      elemento.style.verticalAlign = 'middle';
      elemento.style.overflow = 'hidden';
    });

    // =========================
    // CABEÇALHO
    // =========================

    tabela.querySelectorAll('thead tr').forEach(tr => {
      const elemento = tr as HTMLElement;

      elemento.style.textAlign = 'center';
      elemento.style.verticalAlign = 'middle';
    });

    tabela.querySelectorAll('thead th').forEach(cell => {
      const elemento = cell as HTMLElement;

      elemento.style.backgroundColor = '#d9d9d9';
      elemento.style.color = '#222';
      elemento.style.fontWeight = '700';
      elemento.style.textAlign = 'center';
      elemento.style.verticalAlign = 'middle';
      elemento.style.padding = '2px 2px';
      elemento.style.fontSize = '10pt';
      elemento.style.lineHeight = '1';
      elemento.setAttribute('bgcolor', '#d9d9d9');
    });

    tabela.querySelectorAll('td:nth-child(2)').forEach(cell => {
      const elemento = cell as HTMLElement;

      elemento.style.wordBreak = 'normal';
      elemento.style.overflowWrap = 'break-word';
    });

    // =========================
    // IMPOSTOS
    // =========================

    tabela.querySelectorAll('.coluna-imposto').forEach(cell => {
      const elemento = cell as HTMLElement;

      elemento.style.width = '42px';
      elemento.style.minWidth = '42px';
      elemento.style.maxWidth = '42px';
      elemento.style.boxSizing = 'border-box';

      elemento.style.whiteSpace = 'normal';
      elemento.style.textAlign = 'center';
      elemento.style.verticalAlign = 'middle';
      elemento.style.wordBreak = 'normal';
      elemento.style.overflowWrap = 'normal';

      elemento.setAttribute('width', '42');
    });

    // =========================
    // FINAME / NCM
    // =========================

    tabela.querySelectorAll('tbody td').forEach(cell => {
      const elemento = cell as HTMLElement;

      const texto = elemento.textContent?.trim() || '';

      if (texto) {
        elemento.style.verticalAlign = 'middle';
      }
    });

    const ClipboardItemClass = (window as any).ClipboardItem;

    await (navigator.clipboard as any).write([
      new ClipboardItemClass({
        'text/html': new Blob([tabela.outerHTML], { type: 'text/html' }),
        'text/plain': new Blob([tabela.innerText], { type: 'text/plain' }),
      }),
    ]);

    toast.success('Tabela copiada para a área de transferência!');
  };

  return (
    <Card className="shadow p-3 mb-4" style={{ position: 'relative' }}>
      <IconButton
        onClick={handleToggleAll}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
        }}
        size="small"
        aria-label={allExpanded ? 'Recolher todos' : 'Expandir Equipamentos'}
      >
        <span style={{ marginRight: 6, fontWeight: 600, fontSize: '0.95rem' }}>
          {allExpanded ? 'Recolher todos' : 'Expandir Equipamentos'}
        </span>
        {allExpanded ? <FaChevronUp /> : <FaChevronDown />}
      </IconButton>
      <Subtitle name="Resumo" />
      <CardBody>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginBottom: 10,
          }}
        >
          <Button
            size="sm"
            className="btn-icon"
            style={{
              backgroundColor: '#367c8a',
              borderColor: '#367c8a',
              color: '#fff',
            }}
            type="button"
            onClick={handleCopyBtn}
          >
            Copiar tabela
          </Button>
          {!modoOrdenacao ? (
            <Button
              size="sm"
              className="btn-icon"
              color="primary"
              type="button"
              onClick={iniciarOrdenacao}
            >
              Ordenar
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                className="btn-icon"
                color="success"
                type="button"
                onClick={salvarOrdenacao}
              >
                Salvar ordenação
              </Button>

              <Button
                size="sm"
                className="btn-icon"
                color="danger"
                type="button"
                onClick={cancelarOrdenacao}
              >
                Cancelar
              </Button>
            </>
          )}
        </div>
        <div ref={tabelaRef}>
          <Table
            bordered
            responsive
            // onCopy={handleCopy}
            className="tabela-impostos mb-0"
          >
            <thead>
              <tr className="text-center">
                <th rowSpan={2}>TAG</th>

                <th rowSpan={2}>DESCRIÇÃO</th>

                <th colSpan={5}>IMPOSTOS INCLUSOS</th>

                <th rowSpan={2}>VALOR TOTAL (R$)</th>

                <th rowSpan={2}>FATURAMENTO</th>

                <th rowSpan={2}>
                  CÓDIGO
                  <br />
                  FINAME
                </th>

                <th rowSpan={2}>
                  CÓDIGO
                  <br />
                  NCM
                </th>
              </tr>

              <tr className="text-center">
                <th className="coluna-imposto">IPI</th>
                <th className="coluna-imposto">ICMS</th>
                <th className="coluna-imposto">
                  PIS
                  <br />
                  COFINS
                </th>
                <th className="coluna-imposto">ST</th>
                <th className="coluna-imposto">ISS</th>
              </tr>
            </thead>

            {modoOrdenacao ? (
              <SortableList
                items={itensOrdenaveis}
                onSortEnd={handleSortEnd}
                // disabled={!modoOrdenacao}
                distance={5}
                renderItem={renderLinhaItem}
              />
            ) : (
              <tbody
                style={{
                  padding: 0,
                  margin: 0,
                }}
              >
                {itensOrdenaveis.map(item => (
                  <React.Fragment key={item.id}>
                    {renderLinhaItem(item)}
                    {renderCollapse(item)}
                  </React.Fragment>
                ))}
              </tbody>
            )}

            <tfoot>
              <>
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      paddingRight: 32,
                      color: corValorFinal,
                      transition: 'color 0.3s',
                    }}
                  >
                    Valor final: &nbsp;
                    {formatCurrency(valorFinalAtual || 0)}
                    <Tooltip
                      title={
                        <p>
                          Valor final sem arr.:{' '}
                          {formatCurrency(valorFinalAtualSemArr, 2)}
                        </p>
                      }
                      placement="top"
                      arrow
                    >
                      <IconButton
                        style={{ marginLeft: 4, padding: 0 }}
                        size="small"
                      >
                        <FaInfoCircle size={14} color="#888787" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>

                <tr
                  hidden={
                    informacaoProposta.precificacao.filter(
                      item => item.valoresModificados,
                    )?.length === 0
                  }
                >
                  <td
                    colSpan={10}
                    style={{
                      textAlign: 'right',
                      paddingRight: 32,
                    }}
                  >
                    <Badge color="warning" className="badge-lg" pill>
                      Valor Novo:{' '}
                      {formatCurrency(
                        informacaoProposta.precificacao.reduce(
                          (accumulator, currentValue) =>
                            accumulator +
                            ((currentValue.valorFinal ?? 0) +
                              (currentValue.valorDespachante ?? 0) +
                              (currentValue.valorSeguro ?? 0)),
                          0,
                        ) || 0,
                      )}
                    </Badge>
                  </td>
                </tr>
              </>
            </tfoot>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}
